// Modal de detalhe de uma matéria do aluno. Mostra em painéis:
//   - Cabeçalho: nome, turma, professor(es), badge de status
//   - Inclusão: quando foi matriculado
//   - Pagamentos: pacote ativo + créditos + histórico de compras
//   - Próximas aulas (scheduled futuras)
//   - Aulas feitas (realized passadas)
//
// Visualmente segue o mesmo padrão dos outros modais (Notif/Mensagem/Aula).

import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  BookOpen,
  CalendarCheck,
  CalendarClock,
  CalendarX,
  CheckCircle2,
  ClipboardList,
  Clock,
  CreditCard,
  Flag,
  GraduationCap,
  Library,
  Loader2,
  Receipt,
  Sparkles,
  User,
  UserPlus,
  X,
  XCircle as XCircleIcon
} from "lucide-react";
import {
  api,
  type StudentDisciplinaDetail,
  type StudentDisciplinaEvaluation,
  type StudentDisciplinaEvaluationItem
} from "./api";

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}

export function DisciplinaDetailModal({
  disciplinaId,
  onClose
}: {
  disciplinaId: string;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [err, setErr] = useState<string | null>(null);
  const [d, setD] = useState<StudentDisciplinaDetail | null>(null);

  async function load() {
    setPhase("loading");
    try {
      const r = await api.getMyDisciplina(disciplinaId);
      setD(r.disciplina);
      setPhase("ready");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
  }, [disciplinaId]);

  const upcoming =
    d?.aulas.filter(
      (a) => a.status === "scheduled" && new Date(a.dataHora).getTime() >= Date.now()
    ) ?? [];
  const past =
    d?.aulas.filter(
      (a) =>
        a.status === "realized" ||
        (a.status === "scheduled" && new Date(a.dataHora).getTime() < Date.now())
    ) ?? [];

  const aulasDadas = past.filter((a) => a.status === "realized").length;

  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-start justify-between gap-3 border-b border-border px-6 py-4">
          <div class="flex items-start gap-3 min-w-0">
            <div class="grid size-11 shrink-0 place-items-center rounded-md bg-primary/15 text-primary ring-1 ring-primary/25">
              <GraduationCap className="size-5" />
            </div>
            <div class="min-w-0">
              <h2 class="truncate text-lg font-bold">
                {d?.name ?? "Carregando…"}
              </h2>
              {d?.offering?.turmaName && (
                <p class="mt-0.5 text-xs text-muted-foreground">
                  Turma: {d.offering.turmaName}
                  {d.offering.turmaCode ? ` · ${d.offering.turmaCode}` : ""}
                </p>
              )}
              {d && d.teachers.length > 0 && (
                <p class="mt-0.5 truncate text-xs text-muted-foreground">
                  Com {d.teachers.map((t) => t.name).join(", ")}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {phase === "loading" && (
            <p class="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Carregando dados da
              matéria…
            </p>
          )}
          {phase === "error" && (
            <div class="rounded-md border border-destructive/30 bg-destructive/5 p-4">
              <p class="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="size-4" /> {err}
              </p>
            </div>
          )}

          {phase === "ready" && d && (
            <>
              {/* KPI strip */}
              <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <KpiBox
                  icon={CreditCard}
                  label="Saldo"
                  value={`${d.saldo} aula${d.saldo === 1 ? "" : "s"}`}
                  tone="primary"
                />
                <KpiBox
                  icon={CalendarCheck}
                  label="Dadas"
                  value={String(aulasDadas)}
                  tone="emerald"
                />
                <KpiBox
                  icon={CalendarClock}
                  label="Próximas"
                  value={String(upcoming.length)}
                  tone="blue"
                />
                <KpiBox
                  icon={Receipt}
                  label="Pacotes"
                  value={String(d.compras.length)}
                  tone="amber"
                />
              </div>

              {/* Avaliação composta — só renderiza se houver pelo menos
                  um item ou se passing > 0 (todas disciplinas têm passing).
                  Quando vazia, suprime o card. */}
              <AvaliacaoCard disciplinaId={disciplinaId} />

              {/* Inclusão */}
              <Section icon={UserPlus} title="Matrícula">
                {d.enrollment ? (
                  <p class="text-sm">
                    Você foi matriculado em{" "}
                    <strong>{d.offering.turmaName}</strong> em{" "}
                    <strong>
                      {new Date(d.enrollment.enrolledAt).toLocaleDateString(
                        "pt-BR",
                        { day: "2-digit", month: "long", year: "numeric" }
                      )}
                    </strong>
                    .
                  </p>
                ) : (
                  <p class="text-xs italic text-muted-foreground">
                    Sem dado de matrícula.
                  </p>
                )}
              </Section>

              {/* Pagamentos / Compras */}
              <Section icon={Receipt} title="Pagamentos">
                {d.compras.length === 0 ? (
                  <p class="text-xs italic text-muted-foreground">
                    Nenhuma compra de pacote ainda.
                  </p>
                ) : (
                  <ul class="space-y-1.5">
                    {d.compras.map((c) => (
                      <li
                        key={c.id}
                        class="flex flex-wrap items-center gap-3 rounded-md border border-border bg-background/60 px-3 py-2"
                      >
                        <div class="min-w-0 flex-1">
                          <div class="flex flex-wrap items-center gap-2">
                            <span class="text-sm font-semibold">
                              {c.pacoteNomeSnap}
                            </span>
                            <CompraStatusBadge status={c.status} />
                          </div>
                          <div class="mt-0.5 text-[11px] text-muted-foreground">
                            {c.numAulasSnap} aula{c.numAulasSnap === 1 ? "" : "s"} ·{" "}
                            {formatBRL(c.valorCents)} ·{" "}
                            {c.status === "paid" && c.paidAt
                              ? `Pago em ${new Date(c.paidAt).toLocaleDateString("pt-BR")}`
                              : `Criado em ${new Date(c.createdAt).toLocaleDateString("pt-BR")}`}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {/* Próximas aulas */}
              <Section icon={CalendarClock} title="Próximas aulas">
                {upcoming.length === 0 ? (
                  <p class="text-xs italic text-muted-foreground">
                    Nenhuma aula agendada.
                  </p>
                ) : (
                  <ul class="space-y-1.5">
                    {upcoming.slice(0, 8).map((a) => (
                      <AulaCompactRow key={a.id} a={a} highlight />
                    ))}
                  </ul>
                )}
              </Section>

              {/* Aulas feitas */}
              <Section icon={CheckCircle2} title="Aulas feitas">
                {past.filter((a) => a.status === "realized").length === 0 ? (
                  <p class="text-xs italic text-muted-foreground">
                    Nenhuma aula realizada ainda.
                  </p>
                ) : (
                  <ul class="space-y-1.5">
                    {past
                      .filter((a) => a.status === "realized")
                      .slice(0, 12)
                      .map((a) => (
                        <AulaCompactRow key={a.id} a={a} />
                      ))}
                  </ul>
                )}
              </Section>
            </>
          )}
        </div>

        <footer class="flex items-center justify-end gap-2 border-t border-border bg-muted/20 px-6 py-3">
          <button
            type="button"
            onClick={onClose}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
          >
            Fechar
          </button>
        </footer>
      </div>
    </div>
  );
}

function KpiBox({
  icon: Icon,
  label,
  value,
  tone
}: {
  icon: typeof Clock;
  label: string;
  value: string;
  tone: "primary" | "emerald" | "blue" | "amber";
}) {
  const toneMap = {
    primary: "border-primary/30 bg-primary/5 text-primary",
    emerald:
      "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400",
    blue: "border-blue-500/30 bg-blue-500/5 text-blue-700 dark:text-blue-400",
    amber:
      "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400"
  } as const;
  return (
    <div class={`rounded-md border px-3 py-2 ${toneMap[tone]}`}>
      <div class="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider opacity-90">
        <Icon className="size-3" />
        {label}
      </div>
      <div class="mt-1 text-base font-extrabold tabular-nums text-foreground">
        {value}
      </div>
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  children
}: {
  icon: typeof Clock;
  title: string;
  children: any;
}) {
  return (
    <section>
      <h3 class="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        <Icon className="size-3.5" />
        {title}
      </h3>
      {children}
    </section>
  );
}

function AulaCompactRow({
  a,
  highlight
}: {
  a: import("./api").StudentDisciplinaAulaRow;
  highlight?: boolean;
}) {
  const dt = new Date(a.dataHora);
  const day = dt.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short"
  });
  const time = dt.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit"
  });
  const isRealized = a.status === "realized";
  return (
    <li
      class={`flex flex-wrap items-center gap-3 rounded-md border px-3 py-2 ${
        isRealized
          ? "border-emerald-500/30 bg-emerald-500/5"
          : highlight
          ? "border-primary/40 bg-primary/5"
          : "border-border bg-background/60"
      }`}
    >
      <div
        class={`flex w-16 shrink-0 flex-col items-center justify-center rounded-md px-1.5 py-1 ring-1 ${
          isRealized
            ? "bg-emerald-500/15 text-emerald-700 ring-emerald-500/30 dark:text-emerald-400"
            : "bg-primary/15 text-primary ring-primary/30"
        }`}
      >
        <span class="text-[9px] font-bold uppercase tracking-wider">
          {day.toUpperCase()}
        </span>
        <span class="mt-0.5 text-sm font-extrabold leading-none tabular-nums">
          {time}
        </span>
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
          <User className="size-3" />
          <span>{a.professorName}</span>
          <span>· {a.duracaoMin}min</span>
        </div>
        {a.descricao && (
          <p class="mt-0.5 truncate text-xs">{a.descricao}</p>
        )}
      </div>
      {isRealized && a.realizedAt && (
        <span class="text-[10px] text-muted-foreground">
          dada em {new Date(a.realizedAt).toLocaleDateString("pt-BR")}
        </span>
      )}
    </li>
  );
}

function CompraStatusBadge({
  status
}: {
  status: "pending" | "paid" | "expired" | "canceled";
}) {
  const map = {
    paid: {
      cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
      label: "Pago"
    },
    pending: {
      cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
      label: "Pendente"
    },
    expired: {
      cls: "bg-muted text-muted-foreground",
      label: "Expirado"
    },
    canceled: {
      cls: "bg-muted text-muted-foreground",
      label: "Cancelado"
    }
  } as const;
  const m = map[status];
  return (
    <span class={`rounded px-1.5 py-0.5 text-[10px] font-bold ${m.cls}`}>
      {m.label}
    </span>
  );
}

// =====================
// Avaliação composta — visão do aluno (read-only)
// =====================

function AvaliacaoCard({ disciplinaId }: { disciplinaId: string }) {
  const [data, setData] = useState<StudentDisciplinaEvaluation | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "error" | "empty">(
    "loading"
  );

  useEffect(() => {
    let canceled = false;
    api
      .getDisciplinaEvaluation(disciplinaId)
      .then((r) => {
        if (canceled) return;
        if (r.items.length === 0) {
          setPhase("empty");
        } else {
          setData(r);
          setPhase("ready");
        }
      })
      .catch(() => {
        if (!canceled) setPhase("error");
      });
    return () => {
      canceled = true;
    };
  }, [disciplinaId]);

  if (phase !== "ready" || !data) return null;

  const passingPct = data.disciplina.passingGrade * 10;
  const recoveryPct = data.disciplina.recoveryGrade * 10;
  const finalPct = data.finalScore * 10;
  const statusInfo = {
    aprovado: {
      label: "Aprovado",
      cls: "bg-emerald-500 text-white",
      panelCls:
        "border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-background",
      Icon: CheckCircle2
    },
    recuperacao: {
      label: "Recuperação",
      cls: "bg-amber-500 text-white",
      panelCls:
        "border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-background",
      Icon: ClipboardList
    },
    reprovado: {
      label: "Reprovado",
      cls: "bg-red-500 text-white",
      panelCls: "border-red-500/40 bg-gradient-to-br from-red-500/10 to-background",
      Icon: XCircleIcon
    },
    incompleto: {
      label: "Em andamento",
      cls: "bg-muted-foreground/40 text-foreground",
      panelCls: "border-border bg-card/40",
      Icon: Sparkles
    }
  } as const;
  const s = statusInfo[data.status];

  return (
    <section class={`space-y-3 rounded-md border p-4 ${s.panelCls}`}>
      <div class="flex flex-wrap items-center justify-between gap-3">
        <h3 class="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary">
          <Flag className="size-3.5" /> Avaliação da disciplina
        </h3>
        <span
          class={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${s.cls}`}
        >
          <s.Icon className="size-3" /> {s.label}
        </span>
      </div>

      {/* Hero da nota final */}
      <div class="flex flex-col items-center gap-3 sm:flex-row sm:items-end sm:gap-6">
        <div class="text-center">
          <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Sua nota
          </div>
          <div class="text-4xl font-black tabular-nums">
            {data.finalScore.toFixed(2)}
            <span class="text-sm font-bold text-muted-foreground"> / 10</span>
          </div>
        </div>
        {/* Barra com marcadores de aprovação e recuperação */}
        <div class="min-w-0 flex-1 space-y-1">
          <div class="relative h-3 w-full overflow-hidden rounded-full bg-muted">
            <div
              class={`absolute inset-y-0 left-0 ${
                data.status === "aprovado"
                  ? "bg-emerald-500"
                  : data.status === "recuperacao"
                    ? "bg-amber-500"
                    : data.status === "reprovado"
                      ? "bg-red-500"
                      : "bg-primary/50"
              }`}
              style={{ width: `${Math.min(100, Math.max(0, finalPct))}%` }}
            />
            {data.disciplina.recoveryEnabled && (
              <span
                class="absolute inset-y-0 w-px bg-amber-500/70"
                style={{ left: `${recoveryPct}%` }}
                title={`Recuperação: ${data.disciplina.recoveryGrade.toFixed(2)}`}
              />
            )}
            <span
              class="absolute inset-y-0 w-px bg-emerald-500/70"
              style={{ left: `${passingPct}%` }}
              title={`Aprovação: ${data.disciplina.passingGrade.toFixed(2)}`}
            />
          </div>
          <div class="flex justify-between text-[9px] text-muted-foreground">
            <span>0</span>
            {data.disciplina.recoveryEnabled && (
              <span style={{ marginLeft: `${recoveryPct - 5}%` }}>
                Rec {data.disciplina.recoveryGrade.toFixed(1)}
              </span>
            )}
            <span style={{ marginLeft: "auto", marginRight: `${100 - passingPct - 3}%` }}>
              Apr {data.disciplina.passingGrade.toFixed(1)}
            </span>
            <span>10</span>
          </div>
        </div>
      </div>

      {/* Items */}
      <ul class="space-y-1.5">
        {data.items.map((it) => (
          <EvaluationItemRow key={it.id} item={it} />
        ))}
      </ul>
    </section>
  );
}

function EvaluationItemRow({
  item
}: {
  item: StudentDisciplinaEvaluationItem;
}) {
  const has = item.score != null;
  const tone =
    item.kind === "prova"
      ? "border-primary/30 bg-background/60"
      : "border-violet-500/30 bg-background/60";
  const kindLabel = item.kind === "prova" ? "Prova" : "Atividade";
  return (
    <li class={`flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 ${tone}`}>
      <span
        class={`shrink-0 rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
          item.kind === "prova"
            ? "bg-primary/15 text-primary"
            : "bg-violet-500/15 text-violet-700 dark:text-violet-300"
        }`}
      >
        {kindLabel}
      </span>
      <div class="min-w-0 flex-1">
        <div class="text-sm font-semibold">{item.title}</div>
        {item.pendingReason && !has && (
          <div class="text-[10px] text-muted-foreground">{item.pendingReason}</div>
        )}
        {item.comment && (
          <div class="text-[10px] italic text-muted-foreground">{item.comment}</div>
        )}
      </div>
      <span class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Peso {item.weight.toFixed(2)}
      </span>
      <span
        class={`tabular-nums rounded-md px-2 py-1 text-sm font-black ${
          has
            ? item.score! >= 7
              ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
              : item.score! >= 4
                ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                : "bg-red-500/15 text-red-600 dark:text-red-400"
            : "bg-muted text-muted-foreground"
        }`}
      >
        {has ? item.score!.toFixed(2) : "—"}
      </span>
    </li>
  );
}
